// frontend/src/pages/ProductionSequencePage.tsx
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/store';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useProductionSequence } from '@/hooks/useProductionSequence';
import { useIsAvpOnly } from '@/hooks/useIsAvpOnly';
import type { ProductionOrder } from '@/types/productionSequence';
import { useScrollToCurrentOrder } from '@/hooks/useScrollToCurrentOrder';
import { SequenceHeader } from '@/components/production-sequence/SequenceHeader';
import { SequenceTable } from '@/components/production-sequence/SequenceTable';
import { SequenceTableAvp } from '@/components/production-sequence/SequenceTableAvp';
import { SequenceCardList } from '@/components/production-sequence/SequenceCardList';
import { LineTabsBar } from '@/components/production-sequence/LineTabsBar';
import { SequenceModeTabs } from '@/components/production-sequence/SequenceModeTabs';
import { PriorityConfigPanel } from '@/components/production-sequence/PriorityConfigPanel';
import { RecalcDialog } from '@/components/production-sequence/RecalcDialog';
import { CalendarEditorDrawer } from '@/components/production-sequence/CalendarEditorDrawer';
import { OrderComponentsDrawer } from '@/components/production-sequence/OrderComponentsDrawer';

export default function ProductionSequencePage() {
  const { dark } = useAppStore();
  const { isMobile } = useBreakpoint();
  const { t } = useTranslation();
  const seq = useProductionSequence();

  const [componentsOrderId, setComponentsOrderId] = useState<string | null>(null);
  // OT en vivo: se recomputa vía recalcComponentStatus en cada reorder/recalc, así el
  // drawer refleja el componentesRecalc actual. Si deja de estar visible, se cierra solo.
  const componentsOrder = seq.visibleOrders.find(o => o.id === componentsOrderId) ?? null;

  function handleViewComponents(o: ProductionOrder) {
    setComponentsOrderId(o.id);
    seq.selectOrder(null);
  }
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  // Solo la vista móvil monta este contenedor; en escritorio lo hacen las tablas.
  useScrollToCurrentOrder({ orders: seq.visibleOrders, isLoading: seq.isLoading, scrollRef: mobileScrollRef, enabled: isMobile, resetKey: seq.selectedLine });
  const isAvpOnly = useIsAvpOnly();
  const [panelOpen, setPanelOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Cualquier cambio de selección de línea con un recálculo sin guardar confirma.
  function guardLineChange(action: () => void) {
    if (seq.isRecalcPending && !window.confirm(t('sequence.recalc.changeLineConfirm', { line: seq.selectedLine }))) return;
    action();
  }

  const pageText = dark ? 'text-slate-100' : 'text-slate-900';
  const cardBg = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200';

  const errorBox = seq.isError ? (
    <div className={`mt-3 rounded-xl border p-4 text-[13px] ${dark ? 'bg-red-950/40 border-red-500/30 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
      {t('common.error')}
    </div>
  ) : null;

  const lineTabs = (
    <LineTabsBar
      lines={seq.lines}
      selectedLine={seq.selectedLine}
      dark={dark}
      onSelect={l => guardLineChange(() => seq.setSelectedLine(l))}
    />
  );

  // Selector superior Producción/Fabricación. En Fabricación se oculta la barra de
  // líneas (solo hay una línea: FABRICACION).
  const modeTabs = (
    <div className={`shrink-0 ${isMobile ? '' : 'mb-3'}`}>
      <SequenceModeTabs
        mode={seq.sequenceMode}
        dark={dark}
        onChange={m => guardLineChange(() => seq.setSequenceMode(m))}
      />
    </div>
  );
  const showLineTabs = seq.sequenceMode !== 'fabricacion';

  return (
    /* Marco acotado a la pantalla (la página no scrollea). En escritorio: barra de
       herramientas, cabeceras de columna, paginación y barra de líneas SIEMPRE visibles;
       el título + KPIs se apartan al hacer scroll (van dentro del scroll de la tabla). */
    <div className={`h-full flex flex-col overflow-hidden ${pageText} ${isMobile ? 'gap-4' : ''}`}>
      {modeTabs}
      {isMobile ? (
        <>
          <SequenceHeader seq={seq} dark={dark} />
          {errorBox}
          <div className="flex-1 min-h-0 flex flex-col">
            <div ref={mobileScrollRef} className="flex-1 min-h-0 overflow-y-auto scroll-thin">
              <SequenceCardList orders={seq.visibleOrders} dark={dark} avpOnly={isAvpOnly} />
            </div>
            {showLineTabs && lineTabs}
          </div>
        </>
      ) : (
        <div className={`flex-1 min-h-0 flex flex-col rounded-xl border overflow-hidden shadow-sm ${cardBg}`}>
          {isAvpOnly ? (
            <SequenceTableAvp
              seq={seq}
              dark={dark}
              header={<><SequenceHeader seq={seq} dark={dark} />{errorBox}</>}
            />
          ) : (
            <SequenceTable
              seq={seq}
              dark={dark}
              onConfigure={() => setPanelOpen(true)}
              onOpenCalendar={() => setCalendarOpen(true)}
              header={<><SequenceHeader seq={seq} dark={dark} />{errorBox}</>}
            />
          )}
          {showLineTabs && lineTabs}
        </div>
      )}

      <PriorityConfigPanel
        open={panelOpen}
        dark={dark}
        config={seq.priorityConfig}
        lines={seq.lines}
        lineConfigs={seq.lineConfigs}
        selectedLine={seq.selectedLine}
        onClose={() => setPanelOpen(false)}
        onApply={seq.applyPriorityConfig}
        onReset={seq.resetToAutoOrder}
        onLineConfigChange={seq.updateLineConfigFor}
      />

      <CalendarEditorDrawer
        open={calendarOpen}
        dark={dark}
        calendarDays={seq.calendarDays}
        onToggleDay={seq.toggleCalendarDay}
        onReset={seq.resetCalendar}
        onClose={() => setCalendarOpen(false)}
      />

      {/* Selector de acción al seleccionar una OT: ver componentes o recalcular */}
      {seq.selectedOrder && (
        <RecalcDialog
          selectedOrder={seq.selectedOrder}
          lineConfig={seq.lineConfig}
          canRecalc={seq.canRecalc}
          onRecalc={seq.triggerRecalc}
          onViewComponents={handleViewComponents}
          onDismiss={() => seq.selectOrder(null)}
          dark={dark}
        />
      )}

      <OrderComponentsDrawer
        open={componentsOrder != null}
        order={componentsOrder}
        dark={dark}
        onClose={() => setComponentsOrderId(null)}
      />
    </div>
  );
}
